import React, {useEffect} from 'react';
import {Backdrop, CircularProgress, Snackbar, Stack, Typography, useTheme} from '@mui/material';
import appConfig from "../../config";
import {useSelector} from "react-redux";
import axios from "axios";
import {isEventManager} from "../../hooks/useUserRole";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import {Cancel, CheckCircle, Error} from "@mui/icons-material";
import {formatFileSize} from "../../lib/utils";

export const defaultUploadFileTypes = ['igc','pdf','doc','docx','xls','xlsx','ppt','pptx','csv','cup','sua','txt','dat','cub','jpg','jpeg','gif','png']
export const defaultMaxUploadFileSize = 1024 * 1024 * 50 // 50 MB

export const prepareFileUploadRequest = (files,
                                         allowedUploadFileTypes = defaultUploadFileTypes,
                                         maxUploadFileSize  = defaultMaxUploadFileSize,
) =>{
    return files.map((file) => {
            const ret =  {
                isValidType: (
                    allowedUploadFileTypes && allowedUploadFileTypes.length > 0 ?
                        allowedUploadFileTypes.some((t) => file.name.endsWith(t))
                        :
                        true
                ),
                isValidSize: file.size <= maxUploadFileSize,
                file: file,
            }
            ret.shouldUpload = ret.isValidSize && ret.isValidType
            return ret
        }
    )
}

export const doUploadFile= (token, file, action, eventId, dayId,
                            onSuccess = ()=>{},
                            onError = (error) => {console.error("Error uploading file: ", error)},
                            onProgress = ()=>{},
) => {
    const url = appConfig.serverUrl+'/stadium/file/upload'
    const formData = new FormData();
    formData.append('file', file.file);
    formData.append('eventId', eventId);
    if (dayId) formData.append('dayId', dayId);
    if (action) formData.append('action', action);


    const config = {
        headers: {
            'Authorization': `Bearer ${token}`,
            'content-type': 'multipart/form-data',
        },
        onUploadProgress: function(progressEvent) {
            onProgress(progressEvent)
        }
    };

    axios.post(url, formData, config)
        .then((response) => {
            onSuccess(response)
        })
        .catch((error) => {
            onError(error)
        });
}

const UploadWidget = ({
                        rootNodeName = 'root',
                        dayId,
                        action,
                        allowedUploadFileTypes = defaultUploadFileTypes,
                        maxUploadFileSize = defaultMaxUploadFileSize,
                        notifyDrop = () => {},
                        onUploadComplete = ()=>{},
                        showUploadedFilesInQueue = false,
                        }) => {

    const session = useSelector(state => state.session)
    const event = useSelector(state => state.session.event)
    const token = useSelector(state => state.session.token)

    const [isManager, setIsManager] = React.useState(isEventManager(session,event?.id))
    const [dropTargetOpen, setDropTargetOpen] = React.useState(false);
    const [fileQueue, setFileQueue] = React.useState([])
    const [processedFileQueue, setProcessedFileQueue] = React.useState([])
    const [uploadingFile, setUploadingFile] = React.useState()
    const [uploading,setUploading] = React.useState()
    const [uploadProgress, setUploadProgress] = React.useState(0)

    const theme = useTheme()

    const handleDragEnter = (evt) => {
        preventDefault(evt)
        setDropTargetOpen(true)
    }

    const preventDefault = (evt) => {
        evt.preventDefault()
        evt.stopPropagation()
    }

    const handleDragLeave = (evt) => {
        preventDefault(evt)
        setDropTargetOpen(false)
    }

    const handleDrop = (evt) => {
        preventDefault(evt)
        setDropTargetOpen(false)
        notifyDrop([...(evt.dataTransfer).files])
        handleFiles([...(evt.dataTransfer).files])
    }

    const clearQueue = () => {
        setFileQueue([])
        setProcessedFileQueue([])
    }

    const uploadFile = (file) =>{

        //TODO: for now a quick block to only allow manager access
        if(!isManager) return

        setUploadingFile(file)

        doUploadFile(
            token,
            file,
            action,
            event.id,
            dayId,
            (response) => {
                console.log("*** Successfully uploaded file:");
                console.log(response.data);
                if (showUploadedFilesInQueue) {
                    const newProcessedFileQueue = [...processedFileQueue]
                    newProcessedFileQueue.push(file)
                    setProcessedFileQueue(newProcessedFileQueue)
                }
                setUploadingFile()
                setUploadProgress(0)
                setUploading(false)
                onUploadComplete()
            },
            (error) => {
                console.error("*** Error uploading file: ", error);
                file.error=error
                const newProcessedFileQueue = [...processedFileQueue]
                newProcessedFileQueue.push(file)
                setProcessedFileQueue(newProcessedFileQueue)
                setUploadingFile()
                setUploadProgress(0)
                setUploading(false)
            },
            (progressEvent) => {
                const percentCompleted = Math.round(progressEvent.loaded / progressEvent.total * 100);
                setUploadProgress(percentCompleted);
             },
        )
    }

    const handleFiles = (files) => {
        if (files.length>0) {
            const newQueue =[...fileQueue].concat(prepareFileUploadRequest(files,allowedUploadFileTypes,maxUploadFileSize))
            setFileQueue(newQueue)
        }
    }

    useEffect(() => {
        const root = document.getElementById(rootNodeName)
        root.addEventListener('dragenter',(e)=>{handleDragEnter(e)})
        root.addEventListener('drop',(e)=>{preventDefault(e)})

    }, [])

    useEffect(() => {
        setIsManager(isEventManager(session,event?.id))
    }, [session.services,session.scopes]);

    useEffect(() => {
        if (fileQueue.length>0 && !uploading){
            setUploading(true)
            const newQueue =[...fileQueue]
            const newProcessedFileQueue = [...processedFileQueue]
            let file
            while(newQueue.length>0 && file===undefined){
                file = newQueue.shift()
                if (!file.shouldUpload){
                    newProcessedFileQueue.push(file)
                    file = undefined
                }
            }
            if (file) {
                uploadFile(file)
            }
            else {
                setUploading(false)
            }
            setProcessedFileQueue(newProcessedFileQueue)
            setFileQueue(newQueue)
        }
        if (!uploadingFile && fileQueue.length === 0 && processedFileQueue.length > 0) {
            //TODO: be able to cancel the timer
            setTimeout(()=>{setProcessedFileQueue([])},5000)
        }
    }, [fileQueue,uploading]);

    return (
        <>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={dropTargetOpen}
                onDragLeave={handleDragLeave}
                onDragEnter={preventDefault}
                onDragOver={preventDefault}
                onDrop={handleDrop}
            >
                <Typography
                    variant={'h6'}
                    component={'div'}
                    sx={{
                        width: '90%',
                        height: '90%',
                        border: 'dotted 2px #ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    Drop files to upload
                </Typography>
            </Backdrop>
            {(fileQueue.length>0 || uploadingFile || processedFileQueue.length>0 )&&
                <Paper
                    sx={{
                        position:'fixed',
                        right:0,
                        bottom:0,
                        m:1,
                        p:1,
                        background:'#333333',
                        opacity:.7,
                }}
                    square
                >
                    <Stack spacing={1.5} px={1} py={.5} sx={{alignItems:'stretch'}}>
                        {fileQueue.toReversed().map( (fileRequest, idx) => {
                            const file = fileRequest.file

                            return (
                                <Box key={file.name} sx={{px:1.5, py:.5,background:'#dddddd',display:'flex',alignItems:'center',columnGap:2}}>
                                    <Typography>{file.name}</Typography>
                                    <Typography variant={'body2'}  flexGrow={1} sx={{textAlign:'right'}}>{formatFileSize(file.size)}</Typography>
                                    <Box sx={{display:'flex',alignItems:'center',width:'1em',justifyContent:'flex-end'}}><Cancel sx={{fontSize:'1.2em'}}/></Box>
                                </Box>
                            )
                        })}
                        {uploadingFile &&
                            <Box key={uploadingFile.file.name} sx={{px:1.5, py:.5,background:'#dddddd',display:'flex',alignItems:'center',columnGap:2}}>
                                <Typography>{uploadingFile.file.name}</Typography>
                                <Typography variant={'body2'}  flexGrow={1} sx={{textAlign:'right'}}>{formatFileSize(uploadingFile.file.size)}</Typography>
                                <Box sx={{display:'flex',alignItems:'center',width:'1em',justifyContent:'flex-end'}}><CircularProgress variant="determinate" value={uploadProgress} size={'1em'}/></Box>
                            </Box>
                        }
                        {processedFileQueue.toReversed().map( (fileRequest, idx) => {

                            const file = fileRequest.file
                            const error = fileRequest.error
                            const widget = error ?
                                <Box sx={{display:'flex',alignItems:'center',width:'1em',justifyContent:'flex-end'}}><Error color='error' sx={{fontSize:'1.2em'}}/></Box>
                                :
                                !fileRequest.shouldUpload ?
                                    <Box sx={{display:'flex',alignItems:'center',width:'1em',justifyContent:'flex-end'}}><Error color='error' sx={{fontSize:'1.2em'}}/></Box>
                                    :
                                    <Box sx={{display:'flex',alignItems:'center',width:'1em',justifyContent:'flex-end'}}><CheckCircle color='success' sx={{fontSize:'1.2em'}}/></Box>


                            return (
                                <Box key={file.name} sx={{px:1.5, py:.5,background:'#dddddd',display:'flex',alignItems:'center',columnGap:2}}>
                                    <Typography>{file.name}</Typography>
                                    <Typography variant={'body2'}  flexGrow={1} sx={{textAlign:'right'}}>{formatFileSize(file.size)}</Typography>
                                    {widget}
                                </Box>
                            )
                        })}
                    </Stack>
                </Paper>
            }
        </>
    )
};

export default UploadWidget;